// import { API } from "../../../config/API";

// const homeCache = async () => {};

// const refreshNewsCache = async (id: any) => {
//   let url = API.CACHE_SERVER_4 + API.REFRESH_CACHE + id;
//   let res = await fetch(url, {
//     method: "GET",
//     referrerPolicy: "unsafe-url",
//   });
//   let data = await res.json();
//   if (data?.revalidated) {
//     console.log("Cache revalidated for id:", id);
//   } else {
//     console.log("Couldn't refresh cache for id:", id);
//   }
// };

// export { homeCache, refreshNewsCache };

import { API } from "../../../config/API";
import { message, notification } from "antd";

const homeCache = async () => {};

// Every website server keeps its own cache, so a refresh has to reach each one
// (production serves the site from more than one of these).
const CACHE_SERVERS = [
  API.CACHE_SERVER_1,
  API.CACHE_SERVER_2,
  API.CACHE_SERVER_3,
  API.CACHE_SERVER_4,
].filter(Boolean);
const CACHE_REFRESH_TIMEOUT_MS = 8000;

const hostOf = (server: string) => {
  try {
    return new URL(server).host;
  } catch {
    return server;
  }
};

// A plain GET with no custom headers: anything else (e.g. Cache-Control) makes
// the browser send a CORS preflight, which the website does not allow.
const revalidateOn = async (server: string, endpoint: string) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CACHE_REFRESH_TIMEOUT_MS);
  try {
    const res = await fetch(server + endpoint, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (!data?.revalidated) throw new Error("not revalidated");
    return data;
  } catch (err: any) {
    throw new Error(err?.name === "AbortError" ? "timed out" : err?.message || "failed");
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Refresh the website's cached copy of Today's Articles (/todaysarticle?id=…):
 * page titles and share previews, which otherwise refresh within 60 seconds.
 * Sent to every configured cache server; reports which ones failed.
 */
const refreshArticleCache = async () => {
  if (!CACHE_SERVERS.length) {
    notification.error({
      message: "Cache Refresh Failed",
      description: "No cache servers are configured (REACT_APP_CACHE_SERVER_1..4).",
    });
    return { revalidated: false };
  }
  const hide = message.loading("Refreshing Today's Articles cache…", 0);
  const results = await Promise.allSettled(
    CACHE_SERVERS.map((server) => revalidateOn(server, API.REFRESH_ARTICLE_CACHE))
  );
  hide();

  const failed = results
    .map((r, i) =>
      r.status === "rejected" ? `${hostOf(CACHE_SERVERS[i])} (${r.reason?.message})` : null
    )
    .filter(Boolean);
  const okCount = results.length - failed.length;

  if (okCount === results.length) {
    notification.success({
      message: "Cache Refreshed",
      description: `Today's Articles refreshed on ${okCount} server${okCount > 1 ? "s" : ""}.`,
    });
  } else if (okCount > 0) {
    notification.warning({
      message: "Cache Partly Refreshed",
      description: `Refreshed on ${okCount} of ${results.length} servers. Not reached: ${failed.join(", ")}`,
    });
  } else {
    notification.error({
      message: "Cache Refresh Failed",
      description: `No server could be reached: ${failed.join(", ")}`,
    });
  }
  if (failed.length) console.error("Article cache refresh failed on:", failed);
  return { revalidated: okCount > 0, refreshed: okCount, failed };
};

const refreshNewsCache = async (id: any) => {
  try {
    if (!id) {
      throw new Error("News ID is required");
    }

    let url = API.CACHE_SERVER_4 + API.REFRESH_CACHE + id;

    let res = await fetch(url, {
      method: "GET",
      referrerPolicy: "unsafe-url",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    let data = await res.json();

    if (data?.revalidated) {
      notification.success({
        message: "Cache Refreshed",
        description: "Content has been updated successfully",
      });
      console.log("Cache revalidated for id:", id);
    } else {
      throw new Error("Cache revalidation failed");
    }

    return data;
  } catch (error: any) {
    console.error("Cache refresh failed:", error);
    notification.error({
      message: "Cache Refresh Failed",
      description: error.message || "Please try again later",
    });
    return { revalidated: false, error: error.message };
  }
};

export { homeCache, refreshNewsCache, refreshArticleCache };
